/** 模型提供商（ProviderConfig）仓储：CRUD + 类型/启用状态过滤 + 分页。 */

import type { Table } from 'dexie';

import { getDb } from '../db';
import { newId } from '../ids';
import type { ProviderConfig } from '../types';
import {
  compareNameThenId,
  decodeNameCursor,
  encodeNameCursor,
  normalizeLimit,
  type Page,
  type PageQuery,
} from './pagination';

export interface CreateProviderInput {
  id?: string;
  kind: ProviderConfig['kind'];
  protocol?: ProviderConfig['protocol'];
  vendorId?: string;
  logo?: string;
  name: string;
  baseUrl: string;
  apiKeyRef: string;
  purpose?: ProviderConfig['purpose'];
  defaultModel?: string;
  enabled?: boolean;
}

export interface ProviderQuery extends PageQuery {
  kind?: ProviderConfig['kind'];
  enabled?: boolean;
}

function table(): Table<ProviderConfig, string> {
  return getDb().providers;
}

export function createProvider(input: CreateProviderInput): ProviderConfig {
  return {
    id: input.id ?? newId(),
    kind: input.kind,
    ...(input.protocol !== undefined ? { protocol: input.protocol } : {}),
    ...(input.vendorId !== undefined ? { vendorId: input.vendorId } : {}),
    ...(input.logo !== undefined ? { logo: input.logo } : {}),
    name: input.name,
    baseUrl: input.baseUrl,
    apiKeyRef: input.apiKeyRef,
    enabled: input.enabled ?? true,
    ...(input.purpose !== undefined ? { purpose: input.purpose } : {}),
    ...(input.defaultModel !== undefined ? { defaultModel: input.defaultModel } : {}),
  };
}

export async function putProvider(provider: ProviderConfig): Promise<string> {
  return table().put(provider);
}

export async function createAndSaveProvider(input: CreateProviderInput): Promise<ProviderConfig> {
  const provider = createProvider(input);
  await putProvider(provider);
  return provider;
}

export async function getProvider(id: string): Promise<ProviderConfig | undefined> {
  return table().get(id);
}

export async function listProviders(query: ProviderQuery = {}): Promise<ProviderConfig[]> {
  const all = await table().toArray();
  return all
    .filter((provider) => {
      if (query.kind && provider.kind !== query.kind) return false;
      if (query.enabled !== undefined && provider.enabled !== query.enabled) return false;
      return true;
    })
    .sort(compareNameThenId);
}

/** 按名称分页（游标与列表排序规则一致）。 */
export async function listProvidersPage(query: ProviderQuery = {}): Promise<Page<ProviderConfig>> {
  const limit = normalizeLimit(query.limit);
  const all = await listProviders(query);
  const cursor = query.cursor ? decodeNameCursor(query.cursor) : null;
  const ordered = cursor ? all.filter((p) => compareNameThenId(p, cursor) > 0) : all;

  const items = ordered.slice(0, limit);
  const hasMore = ordered.length > limit;
  const last = items[items.length - 1];
  return {
    items,
    hasMore,
    nextCursor: hasMore && last ? encodeNameCursor(last.name, last.id) : null,
  };
}

export async function updateProvider(
  id: string,
  patch: Partial<Omit<ProviderConfig, 'id'>>,
): Promise<ProviderConfig | undefined> {
  const existing = await getProvider(id);
  if (!existing) return undefined;
  const next: ProviderConfig = { ...existing, ...patch, id };
  await putProvider(next);
  return next;
}

export async function deleteProvider(id: string): Promise<void> {
  await table().delete(id);
}

export async function countProviders(): Promise<number> {
  return table().count();
}

export async function clearProviders(): Promise<void> {
  await table().clear();
}
